import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import DashboardTemplate from '../../Common/DashboardTemplate';
import { Button, CircularProgress, TextField, Tabs, Tab } from '@material-ui/core';
import { ServiceLocator } from '../../../Services/ServiceLocator';
import SortableTableHeader from '../../Common/SortableTableHeader';

const styles = theme => ({
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flex: 1,
    },
    toolbarRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    typeBadge: {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    customerBadge: {
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
    },
    serviceBadge: {
        backgroundColor: '#f3e5f5',
        color: '#6a1b9a',
    },
});

const CART_FILTER_ALL = 'all';
const CART_FILTER_CUSTOMER = 'customer';
const CART_FILTER_SERVICE = 'service';

class CustomerCarts extends Component {
    state = {
        loading: true,
        carts: [],
        search: '',
        cartFilter: CART_FILTER_ALL,
        sortField: 'updatedUtc',
        sortDirection: 'desc',
    };

    componentDidMount() {
        this.loadData();
    }

    loadData = () => {
        this.setState({ loading: true });
        ServiceLocator.Instance.getProductListingService()
            .getCarts()
            .then(carts => this.setState({ carts, loading: false }))
            .catch(err => {
                window.$alertError(err.message || 'Failed to load customer carts');
                this.setState({ loading: false });
            });
    };

    handleSort = (field) => {
        this.setState(prev => ({
            sortField: field,
            sortDirection: prev.sortField === field && prev.sortDirection === 'asc' ? 'desc' : 'asc',
        }));
    };

    openCart = (id) => {
        this.props.history.push(`/admin/customer-carts/${id}`);
    };

    createCart = () => {
        this.props.history.push('/admin/customer-carts/new');
    };

    getFilteredCarts = () => {
        const { carts, search, cartFilter, sortField, sortDirection } = this.state;
        const term = search.trim().toLowerCase();

        let rows = carts.filter(c => {
            if (cartFilter === CART_FILTER_CUSTOMER && !c.isCustomerCart) return false;
            if (cartFilter === CART_FILTER_SERVICE && c.isCustomerCart) return false;
            if (!term) return true;
            return (c.shareToken || '').toLowerCase().includes(term)
                || (c.notes || '').toLowerCase().includes(term)
                || (c.customerEmail || '').toLowerCase().includes(term)
                || (c.customerName || '').toLowerCase().includes(term);
        });

        rows = rows.sort((a, b) => {
            const dir = sortDirection === 'asc' ? 1 : -1;
            const av = a[sortField];
            const bv = b[sortField];
            if (av == null && bv == null) return 0;
            if (av == null) return dir;
            if (bv == null) return -dir;
            if (typeof av === 'string') return av.localeCompare(bv) * dir;
            if (typeof av === 'boolean') return ((av === bv) ? 0 : av ? 1 : -1) * dir;
            return (av > bv ? 1 : av < bv ? -1 : 0) * dir;
        });

        return rows;
    };

    renderTypeBadge = (cart) => {
        const { classes } = this.props;
        if (cart.isCustomerCart) {
            return <span className={`${classes.typeBadge} ${classes.customerBadge}`}>Customer</span>;
        }
        return <span className={`${classes.typeBadge} ${classes.serviceBadge}`}>Service</span>;
    };

    render() {
        const { classes } = this.props;
        const { loading, search, cartFilter } = this.state;
        const rows = this.getFilteredCarts();

        return (
            <DashboardTemplate title="Customer Carts" onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    <div className="container">
                        <div className={`${classes.toolbar} toolbar-responsive`} style={{marginBottom: 10}}>
                            <div className={`${classes.toolbarLeft} toolbar-left`}>
                                <TextField
                                    value={search}
                                    onChange={e => this.setState({ search: e.target.value })}
                                    label="Search customer, token, or notes..."
                                    variant="outlined"
                                    className="search-field"
                                    style={{ minWidth: '300px' }}
                                />
                            </div>
                            <div className={`${classes.toolbarRight} toolbar-right`}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={this.createCart}
                                    className="success-button-lg action-button"
                                    style={{ minWidth: 120 }}
                                >
                                    New cart
                                </Button>
                            </div>
                        </div>
                        <Tabs
                            value={cartFilter}
                            onChange={(_, value) => this.setState({ cartFilter: value })}
                            indicatorColor="primary"
                            textColor="primary"
                            style={{ marginBottom: 16 }}
                        >
                            <Tab label="All carts" value={CART_FILTER_ALL} />
                            <Tab label="Live customer carts" value={CART_FILTER_CUSTOMER} />
                            <Tab label="Service share carts" value={CART_FILTER_SERVICE} />
                        </Tabs>

                        {loading ? (
                            <center style={{ padding: '40px 0' }}><CircularProgress /></center>
                        ) : (
                            <>
                                <div className="parts-table-container" style={{marginTop: 10}}>
                                    <table className="parts-table">
                                        <thead>
                                            <tr>
                                                <SortableTableHeader field="isCustomerCart" sortField={this.state.sortField} sortDirection={this.state.sortDirection} onSort={this.handleSort}>Type</SortableTableHeader>
                                                <SortableTableHeader field="customerName" sortField={this.state.sortField} sortDirection={this.state.sortDirection} onSort={this.handleSort}>Customer</SortableTableHeader>
                                                <SortableTableHeader field="lineItemCount" sortField={this.state.sortField} sortDirection={this.state.sortDirection} onSort={this.handleSort}>Lines</SortableTableHeader>
                                                <SortableTableHeader field="notes" sortField={this.state.sortField} sortDirection={this.state.sortDirection} onSort={this.handleSort}>Notes</SortableTableHeader>
                                                <SortableTableHeader field="updatedUtc" sortField={this.state.sortField} sortDirection={this.state.sortDirection} onSort={this.handleSort}>Updated</SortableTableHeader>
                                                <SortableTableHeader field="expiresUtc" sortField={this.state.sortField} sortDirection={this.state.sortDirection} onSort={this.handleSort}>Expires</SortableTableHeader>
                                                <th style={{ minWidth: 80 }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map(c => (
                                                <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => this.openCart(c.id)}>
                                                    <td>{this.renderTypeBadge(c)}</td>
                                                    <td>
                                                        {c.isCustomerCart ? (
                                                            <>
                                                                <div>{c.customerName || '—'}</div>
                                                                <div style={{ fontSize: '0.85rem', color: '#666' }}>{c.customerEmail || ''}</div>
                                                            </>
                                                        ) : (
                                                            <span style={{ color: '#888' }}>—</span>
                                                        )}
                                                    </td>
                                                    <td>{c.lineItemCount}</td>
                                                    <td>{c.notes || ''}</td>
                                                    <td>{c.updatedUtc ? new Date(c.updatedUtc).toLocaleString() : ''}</td>
                                                    <td>{c.expiresUtc ? new Date(c.expiresUtc).toLocaleString() : '—'}</td>
                                                    <td>
                                                        <Button
                                                            size="small"
                                                            className="success-button-lg"
                                                            onClick={(e) => { e.stopPropagation(); this.openCart(c.id); }}
                                                        >
                                                            {c.isCustomerCart ? 'View' : 'Edit'}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {rows.length === 0 && (
                                    <p style={{ textAlign: 'center', padding: '24px 0', color: '#666' }}>
                                        {cartFilter === CART_FILTER_CUSTOMER
                                            ? 'No live customer carts right now. Carts appear here when logged-in customers add items on the storefront.'
                                            : 'No customer carts yet. Click New cart to build a shareable cart for customer service.'}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </DashboardTemplate>
        );
    }
}

CustomerCarts.propTypes = {
    classes: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
};

export default withStyles(styles)(CustomerCarts);
